import React, { useState } from "react";
import { useFormikContext, ErrorMessage, Field } from "formik";
import { RadioGroup, Checkbox as Check } from "formik-material-ui";
import { makeStyles, Typography, Radio, FormControlLabel, Collapse, useMediaQuery, useTheme } from "@material-ui/core";
import { GoogleAutoComplete } from "ui/googleAutoComplete/GoogleAutoComplete";

import CustomInput from "ui/customFormikInput/CustomInput";

export default function AddressForm({ displayBillingInformation, onAdressChange }) {
  const classes = useStyle();
  const { values, setFieldValue } = useFormikContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const [addressError, setAddressError] = useState(null);
  const [displayInputs, setDisplayInputs] = useState(false);

  const resetFields = () => {
    setFieldValue("full_address", "");
    setFieldValue("street_number", "");
    setFieldValue("street", "");
    setFieldValue("zipcode", "");
    setFieldValue("city", "");
  };

  const getGoogleField = (address, fieldName) => {
    return address.address_components.find((component) => component.types.includes(fieldName))?.long_name;
  };

  const handleChangeAddress = (address) => {
    if (!address.address_components) {
      resetFields();
      setAddressError("Merci de sélectionner votre adresse dans la liste de propositions");
    } else if (!getGoogleField(address, "postal_code")) {
      resetFields();
      setAddressError("Merci de sélectionner une adresse avec un code postal valide");
    } else if (!getGoogleField(address, "street_number")) {
      setDisplayInputs(true);
      setAddressError("Merci de sélectionner un numéro de rue");
      setFieldValue("address_input_text", address.formatted_address);
      setFieldValue("full_address", address.formatted_address);
      setFieldValue("street", getGoogleField(address, "route"));
      setFieldValue("zipcode", getGoogleField(address, "postal_code"));
      setFieldValue("city", getGoogleField(address, "locality"));
    } else {
      setDisplayInputs(false);
      setAddressError(null);
      setFieldValue("address_input_text", address.formatted_address);
      setFieldValue("full_address", address.formatted_address);
      setFieldValue("street_number", getGoogleField(address, "street_number"));
      setFieldValue("street", getGoogleField(address, "route"));
      setFieldValue("zipcode", getGoogleField(address, "postal_code"));
      setFieldValue("city", getGoogleField(address, "locality"));
    }

    onAdressChange && onAdressChange();
  };

  const mounted = React.useRef(true);
  React.useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  return (
    <div className={classes.container}>
      <div className={classes.fullSize}>
        {mounted.current && (
          <GoogleAutoComplete onChange={handleChangeAddress}>
            <CustomInput
              fullWidth
              label="Votre adresse"
              name="address_input_text"
              type="text"
              placeholder="Saisissez votre adresse"
              className={classes.fullSize}
            />
          </GoogleAutoComplete>
        )}
        {addressError ? (
          <Typography variant="caption" color="error">
            {addressError}
          </Typography>
        ) : (
          <Typography variant="caption" color="error">
            <ErrorMessage name="full_address" />
          </Typography>
        )}
      </div>
      {displayInputs && (
        <CustomInput
          name="street_number"
          type="text"
          label={"Numéro"}
          className={classes.fullSize}
          onChange={(event) => {
            setAddressError(null);
            setFieldValue("street_number", event.target.value);
          }}
        />
      )}
      <CustomInput
        error={<ErrorMessage name="informations" />}
        name="informations"
        type="text"
        label={isMobile ? "Infos complémentaires" : "Informations complémentaires"}
        placeholder="Digicode, bâtiment, complément..."
        className={classes.fullSize}
      />
      <div className={classes.fullSize}>
        <Typography variant="body2">Habitez-vous dans un immeuble ?</Typography>
        <Field component={RadioGroup} name="isBuilding" className={classes.radioGroup}>
          <FormControlLabel
            value
            control={<Radio checked={values.isBuilding === "true"} />}
            label={<Typography variant="body2">Oui</Typography>}
          />
          <FormControlLabel
            value={false}
            control={<Radio checked={values.isBuilding === "false"} />}
            label={<Typography variant="body2">Non</Typography>}
          />
        </Field>
        <Typography color="error" variant="caption">
          <ErrorMessage name="isBuilding" />
        </Typography>
      </div>
      <Collapse in={values.isBuilding === "true"} className={classes.collapse}>
        <div>
          <Typography variant="body2">Un ascenseur est-il disponible ?</Typography>
          <Field component={RadioGroup} name="elevator" className={classes.radioGroup}>
            <FormControlLabel
              value
              control={<Radio checked={values.elevator === "true"} />}
              label={<Typography variant="body2">Oui</Typography>}
            />
            <FormControlLabel
              value={false}
              control={<Radio checked={values.elevator === "false"} />}
              label={<Typography variant="body2">Non</Typography>}
            />
          </Field>
          <Typography color="error" variant="caption">
            <ErrorMessage name="elevator" />
          </Typography>
        </div>
        <CustomInput
          error={<ErrorMessage name="floor" />}
          name="floor"
          type="text"
          label={isMobile ? "Précisez votre étage" : "Précisez l’étage auquel vous habitez"}
          className={classes.fullSize}
        />
        <CustomInput
          error={<ErrorMessage name="difficulties" />}
          name="difficulties"
          type="text"
          rows={3}
          multiline
          label="Des difficultés à prévoir ?"
          placeholder="Ex: Escalier étroit"
          className={classes.fullSize}
        />
      </Collapse>
      {displayBillingInformation && (
        <>
          <div
            className={`${classes.fullinline} 
      ${values.isBuilding === "true" ? "" : classes.up}`}
          >
            <FormControlLabel
              control={
                <Field
                  component={Check}
                  color="secondary"
                  disableRipple
                  type="checkbox"
                  name="billing_adress_is_same"
                  className={classes.check}
                />
              }
              label={<Typography variant="body2">Ceci est également mon adresse de facturation</Typography>}
            />
          </div>
          <Collapse in={!values.billing_adress_is_same} className={`${classes.collapse} ${classes.up}`}>
            <div className={classes.containerBilling}>
              <CustomInput
                error={<ErrorMessage name="billing_first_name" />}
                name="billing_first_name"
                type="text"
                label="Votre prénom"
                autoComplete="given-name"
                className={classes.semiSize}
              />
              <CustomInput
                error={<ErrorMessage name="billing_last_name" />}
                name="billing_last_name"
                type="text"
                label="Votre nom"
                autoComplete="family-name"
                className={classes.semiSize}
              />
              <CustomInput
                error={<ErrorMessage name="billing_street" />}
                name="billing_street"
                type="text"
                label="Votre adresse"
                autoComplete="address-line1"
                className={classes.fullSize}
              />
              <CustomInput
                error={<ErrorMessage name="billing_zipcode" />}
                name="billing_zipcode"
                type="text"
                inputProps={{ pattern: "[0-9]*", inputmode: "numeric" }}
                label="Code postal "
                autoComplete="postal-code"
              />
              <CustomInput
                error={<ErrorMessage name="billing_city" />}
                name="billing_city"
                type="text"
                label="Ville"
                autoComplete="address-level2"
              />
            </div>
          </Collapse>
        </>
      )}
    </div>
  );
}

const useStyle = makeStyles((theme) => ({
  fullinline: {
    display: "flex",
    alignItems: "center",
    gridColumn: "1/3",
    marginTop: 0,
    transition: "all 0.2s linear",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1",
    },
  },
  up: {
    marginTop: -40,
  },
  fullSize: {
    gridColumn: "1/3",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1",
    },
  },
  collapse: {
    gridColumn: "1/3",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1",
    },
    "& .MuiCollapse-wrapperInner": {
      display: "grid",
      gridTemplateColumns: "1fr",
      gridRowGap: theme.spacing(3),
    },
  },
  container: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: theme.spacing(3),
    gridRowGap: theme.spacing(3),
    marginTop: theme.spacing(5),
    [theme.breakpoints.up("sm")]: {
      width: "70%",
    },
    [theme.breakpoints.up("md")]: {
      width: "100%",
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  containerBilling: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gridColumnGap: theme.spacing(3),
    gridRowGap: theme.spacing(3),
    marginTop: theme.spacing(5),
    [theme.breakpoints.up("sm")]: {
      width: "100%",
    },
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "1fr",
    },
  },
  inputContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(),
  },
  check: {
    marginLeft: theme.spacing(),
    paddingLeft: 0,
  },
  radioGroup: {
    flexDirection: "row",
    gridColumn: "1/3",
    [theme.breakpoints.down("sm")]: {
      gridColumn: "1",
    },
  },
}));
